import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { PlusLg, BoxArrowUpRight } from 'react-bootstrap-icons';
import { useSelector } from 'react-redux';
import AdminLayout, { isStaff } from '../AdminLayout';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../../common/ui/StateBlock';
import notify, { currentLanguage } from '../../../common/ui/notify';
import { formatDateTime, localInputToUtc } from '../../../common/format';
import DateTimePicker from '../../../common/DateTimePicker';
import './AdminAuction.css';

const L = { geo: 0, en: 1, ru: 2, am: 3 };
const t = (geo, en, ru, am) => [geo, en, ru, am][L[currentLanguage()] ?? 0];
const TONE = { scheduled: 'cx-badge-new', live: 'cx-badge-sale', paused: 'cx-badge-neutral', finished: 'cx-badge-sold', cancelled: 'cx-badge-danger' };

const AdminAuctionEvents = () => {
    const user = useSelector((s) => s.user);
    const [events, setEvents] = useState([]);
    const [state, setState] = useState('loading');
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ title: '', starts_at: '', usd_rate: '', lot_duration_seconds: '15' });

    // Prefill the USD→GEL rate from NBG; the operator can still override it.
    useEffect(() => {
        axios.get('/api/auction/admin/nbg-rate')
            .then((r) => setForm((p) => (p.usd_rate ? p : { ...p, usd_rate: String(r.data.usd_rate) })))
            .catch(() => {});
    }, []);

    const load = useCallback(() => {
        setState('loading');
        axios.get('/api/auction/admin/events')
            .then((res) => { setEvents(res.data.events || []); setState('ready'); })
            .catch(() => setState('error'));
    }, []);

    useEffect(() => { if (isStaff(user)) load(); }, [load, user]);

    const create = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await axios.post('/api/auction/admin/events', {
                title: form.title, starts_at: localInputToUtc(form.starts_at), usd_rate: form.usd_rate || undefined,
                lot_duration_seconds: form.lot_duration_seconds || undefined,
            });
            notify.success(t('ივენთი შეიქმნა', 'Event created', 'Событие создано', 'Իվենթը ստեղծվեց'));
            setForm({ title: '', starts_at: '', usd_rate: '' });
            load();
        } catch (err) { notify.apiError(err); }
        finally { setBusy(false); }
    };

    const body = () => {
        if (state === 'loading') return <LoadingBlock label={t('იტვირთება...', 'Loading...', 'Загрузка...', 'Բեռնվում...')} />;
        if (state === 'error') return <ErrorBlock title={t('შეცდომა', 'Error', 'Ошибка', 'Սխալ')} retryLabel={t('თავიდან', 'Retry', 'Повтор', 'Կրկին')} onRetry={load} />;
        if (!events.length) return <EmptyBlock title={t('ივენთი არ არის', 'No events', 'Нет событий', 'Իվենթներ չկան')} />;
        return (
            <div className="admin-table-wrapper">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>{t('დასახელება', 'Title', 'Название', 'Վերնագիր')}</th>
                            <th className="admin-hide-sm">{t('დაწყება', 'Starts', 'Начало', 'Սկիզբ')}</th>
                            <th>{t('სტატუსი', 'Status', 'Статус', 'Կարգավիճակ')}</th>
                            <th className="admin-table-actions-col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {events.map((ev) => (
                            <tr key={ev.id}>
                                <td><span className="admin-table-primary">{ev.title || `#${ev.id}`}</span></td>
                                <td className="admin-hide-sm admin-table-secondary">{formatDateTime(ev.starts_at)}</td>
                                <td><span className={`cx-badge ${TONE[ev.status] || 'cx-badge-neutral'}`}>{ev.status}</span></td>
                                <td>
                                    <Link to={`/admin/auction/events/${ev.id}`} className="cx-btn cx-btn-subtle cx-btn-sm">
                                        <BoxArrowUpRight /> {t('კონსოლი', 'Console', 'Консоль', 'Կոնսոլ')}
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <AdminLayout title={t('აუქციონები', 'Auctions', 'Аукционы', 'Աճուրդ')}
            subtitle={t('ივენთების შექმნა და მართვა', 'Create and manage events', 'Создание и управление событиями', 'Իվենթների ստեղծում և կառավարում')}>
            <form className="cx-card admin-auction-form" onSubmit={create}>
                <h4>{t('ახალი ივენთი', 'New event', 'Новое событие', 'Նոր իվենթ')}</h4>
                <div className="admin-auction-form-row">
                    <input className="cx-input" required placeholder={t('დასახელება', 'Title', 'Название', 'Վերնագիր')} value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
                    <DateTimePicker value={form.starts_at} onChange={(v) => setForm((p) => ({ ...p, starts_at: v }))} />
                    <input className="cx-input" type="number" step="0.0001" min="0" title={t('USD→GEL კურსი (ეროვნული ბანკი)', 'USD→GEL rate (NBG)', 'Курс USD→GEL (НБГ)', 'USD→GEL (NBG)')} placeholder={t('USD კურსი (NBG)', 'USD rate (NBG)', 'Курс USD (НБГ)', 'USD փոխարժեք (NBG)')} value={form.usd_rate} onChange={(e) => setForm((p) => ({ ...p, usd_rate: e.target.value }))} />
                    <input className="cx-input" type="number" min="5" max="3600" title={t('ბიდის ფანჯარა (წმ)', 'Bid window (sec)', 'Окно ставки (сек)', 'Հայտի պատուհան (վրկ)')} placeholder={t('ბიდის ფანჯარა (წმ)', 'Bid window (sec)', 'Окно (сек)', 'Պատուհան (վրկ)')} value={form.lot_duration_seconds} onChange={(e) => setForm((p) => ({ ...p, lot_duration_seconds: e.target.value }))} />
                    <button className="cx-btn cx-btn-primary" disabled={busy}><PlusLg /> {t('შექმნა', 'Create', 'Создать', 'Ստեղծել')}</button>
                </div>
            </form>

            {body()}
        </AdminLayout>
    );
};

export default AdminAuctionEvents;
